import React, { useMemo, useState } from 'react';
import { Box, Typography, Pressable } from '@/shared/design-system/primitives';
import { toLogicDate } from '@/utils/date-utils';
import { useMetabolicStore } from '../../../stores/v4/useMetabolicStore';
import { Check } from 'lucide-react';
import { DrawingUtils } from '@/shared/logic/metabolic/v4/drawing-utils';
import { ScrollView } from 'react-native';

interface MacronutrientTimelineProps {
    targetDate: Date;
    simulation?: any[]; 
}

/**
 * MacronutrientTimeline: Visualizes high-fidelity metabolic flux.
 * FIXED: Strictly clamps rendering to the 0-100% visible window.
 */
export const MacronutrientTimeline: React.FC<MacronutrientTimelineProps> = ({ targetDate, simulation: injectedSim }) => {
    const storeSimulation = useMetabolicStore(state => state.dailySnapshot.simulation);
    const simulation = injectedSim || storeSimulation;
    
    const [visibleLines, setVisibleLines] = useState<Record<string, boolean>>({
        protein: true, glucose: true, fructose: true, starch: true, fat: true, gastric: true, insulin: true, windows: true, cgm: false
    });

    const toggleLine = (key: string) => setVisibleLines(prev => ({ ...prev, [key]: !prev[key] }));

    const numericStyle = { fontVariantNumeric: 'lining-nums', fontFamily: 'ui-sans-serif, system-ui, sans-serif' };

    // CLAMP DATA: Use full simulation for math, but filter for rendering
    const visiblePoints = useMemo(() => {
        if (!simulation) return [];
        return simulation.filter(p => p.x >= 0 && p.x <= 100);
    }, [simulation]);

    // 1. SCALING ENGINE
    const FIXED_MAX_Y = useMemo(() => {
        if (visiblePoints.length === 0) return 50;
        const maxMacro = Math.max(...visiblePoints.map(p => 
            Math.max(
                p.state.fractions.intact_protein.blood + p.state.fractions.free_amino_acids.blood, 
                p.state.fractions.glucose.blood + p.state.fractions.fructose.blood + p.state.fractions.complex_carbs.blood, 
                p.state.fractions.lipids.blood
            )
        ));
        return Math.max(maxMacro * 1.4, 40);
    }, [visiblePoints]);

    const MAX_GASTRIC = useMemo(() => {
        if (visiblePoints.length === 0) return 100;
        return Math.max(...visiblePoints.map((p: any) => p.state.gastricVolume || 0), 100) * 1.1;
    }, [visiblePoints]);

    const FIXED_MAX_INSULIN = 150; 

    const scaleY = (val: number) => 98 - Math.min((val / FIXED_MAX_Y) * 98, 98);
    const scaleGastric = (val: number) => 98 - Math.min((val / MAX_GASTRIC) * 98, 98);
    const scaleInsulin = (val: number) => 98 - Math.min((val / FIXED_MAX_INSULIN) * 98, 98);

    // 2. DATA DERIVATION
    const macroPeaks = useMemo(() => {
        const types = [
            { id: 'protein', color: '#3b82f6' },
            { id: 'glucose', color: '#2ecc71' },
            { id: 'fructose', color: '#00ffff' },
            { id: 'starch', color: '#a855f7' },
            { id: 'fat', color: '#ef4444' }
        ];
        const rawPeaks = DrawingUtils.calculateLocalMaxima(visiblePoints, types, visibleLines, scaleY);
        const insulinPeaks = DrawingUtils.calculateLocalMaxima(visiblePoints, [{ id: 'insulin', color: '#f29b11' }], visibleLines, scaleInsulin);
        return DrawingUtils.staggerLabels([...rawPeaks, ...insulinPeaks]);
    }, [visiblePoints, visibleLines, FIXED_MAX_Y]);

    const paths = useMemo(() => {
        if (visiblePoints.length === 0) return { protein: "", glucose: "", fructose: "", starch: "", fat: "", gastric: "", insulin: "", cgm: "" };
        let proteinPath = ""; let glucosePath = ""; let fructosePath = ""; let starchPath = ""; let fatPath = ""; let gastricPath = ""; let insulinPath = ""; let cgmPath = "";

        visiblePoints.forEach((p, i) => {
            const py = scaleY(p.state.fractions.intact_protein.blood + p.state.fractions.free_amino_acids.blood);
            const gy = scaleY(p.state.fractions.glucose.blood);
            const fry = scaleY(p.state.fractions.fructose.blood);
            const cxy = scaleY(p.state.fractions.complex_carbs.blood);
            const fy = scaleY(p.state.fractions.lipids.blood);
            const iny = scaleInsulin(p.state.insulin);
            const cgmy = scaleY(p.state.fractions.glucose.interstitium);
            const gastY = scaleGastric(p.state.gastricVolume || 0);

            const prefix = i === 0 ? "M" : "L";
            proteinPath += ` ${prefix} ${p.x} ${py}`;
            glucosePath += ` ${prefix} ${p.x} ${gy}`;
            fructosePath += ` ${prefix} ${p.x} ${fry}`;
            starchPath += ` ${prefix} ${p.x} ${cxy}`;
            fatPath += ` ${prefix} ${p.x} ${fy}`;
            insulinPath += ` ${prefix} ${p.x} ${iny}`;
            cgmPath += ` ${prefix} ${p.x} ${cgmy}`;
            gastricPath += ` ${prefix} ${p.x} ${gastY}`;
        });

        if (visiblePoints.length > 0) {
            gastricPath += ` L ${visiblePoints[visiblePoints.length - 1].x} 98 L ${visiblePoints[0].x} 98 Z`;
        }

        return { protein: proteinPath, glucose: glucosePath, fructose: fructosePath, starch: starchPath, fat: fatPath, gastric: gastricPath, insulin: insulinPath, cgm: cgmPath };
    }, [visiblePoints, FIXED_MAX_Y, MAX_GASTRIC]);

    const formatTime = (xPercent: number) => {
        const mins = (xPercent / 100) * 1440;
        return `${Math.floor(mins / 60).toString().padStart(2, '0')}:${Math.floor(mins % 60).toString().padStart(2, '0')}`;
    };

    const stateWindows = useMemo(() => {
        const windows: any[] = [];
        visiblePoints.forEach((p: any) => {
            const protein = p.state.fractions.intact_protein.blood + p.state.fractions.free_amino_acids.blood;
            const carbs = p.state.fractions.glucose.blood + p.state.fractions.fructose.blood + p.state.fractions.complex_carbs.blood;
            if (carbs > 1.0 && protein > 0.5 && p.state.gastricVolume < 30) {
                windows.push({ x: p.x, type: 'ANABOLIC', color: 'rgba(59, 130, 246, 0.15)' });
            }
        });
        const blocks: any[] = []; let currentBlock: any = null;
        windows.forEach((w: any) => {
            if (!currentBlock || currentBlock.type !== w.type || (w.x - currentBlock.endX > 1.0)) {
                if (currentBlock) blocks.push(currentBlock);
                currentBlock = { type: w.type, startX: w.x, endX: w.x, color: w.color };
            } else currentBlock.endX = w.x;
        });
        if (currentBlock) blocks.push(currentBlock);
        return blocks.filter((b: any) => (b.endX - b.startX) > 2);
    }, [visiblePoints]);

    const isToday = toLogicDate(targetDate) === toLogicDate(new Date());
    const [y, mon, d] = toLogicDate(targetDate).split('-').map(Number);
    const targetMidnightUnix = new Date(y, mon - 1, d).getTime();
    const nowX = isToday ? ((new Date().getTime() - targetMidnightUnix) / (1440 * 60000)) * 100 : null;

    return (
        <Box style={{ height: 300, marginTop: 10, position: 'relative', backgroundColor: 'rgba(255,255,255,0.01)', borderRadius: 12, padding: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.05)' }}>
            <Box style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 15, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.05)', paddingBottom: 10 } as any}>
                {[
                    { id: 'protein', label: 'PROTEIN', color: '#3b82f6' },
                    { id: 'glucose', label: 'GLUCOSE', color: '#2ecc71' },
                    { id: 'cgm', label: 'CGM (LAG)', color: '#2ecc71' },
                    { id: 'fructose', label: 'FRUCTOSE', color: '#00ffff' },
                    { id: 'starch', label: 'STARCH', color: '#a855f7' },
                    { id: 'fat', label: 'FAT', color: '#ef4444' },
                    { id: 'insulin', label: 'INSULIN', color: '#f29b11' },
                    { id: 'gastric', label: 'STOMACH', color: '#555' },
                    { id: 'windows', label: 'WINDOWS', color: '#3b82f6' },
                ].map(item => (
                    <Pressable key={item.id} onPress={() => toggleLine(item.id)}
                        style={{ flexDirection: 'row', alignItems: 'center', gap: 4, opacity: visibleLines[item.id] ? 1 : 0.3, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 4, backgroundColor: visibleLines[item.id] ? 'rgba(255,255,255,0.03)' : 'transparent' }}
                    >
                        <Box style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: item.color, alignItems: 'center', justifyContent: 'center' }}>
                            {visibleLines[item.id] && <Check size={8} color="#fff" strokeWidth={4} />}
                        </Box>
                        <Typography style={{ fontSize: 9, fontWeight: '900', color: item.color }}>{item.label}</Typography>
                    </Pressable>
                ))}
            </Box>

            <Box style={{ position: 'absolute', left: -25, top: 105, height: 120, width: 30, zIndex: 5, pointerEvents: 'none' } as any}>
                <Typography style={{ position: 'absolute', right: 5, top: -5, fontSize: 8, fontWeight: '900', color: '#666' }}>{FIXED_MAX_Y.toFixed(1)}</Typography>
                <Typography style={{ position: 'absolute', right: 5, top: 55, fontSize: 8, fontWeight: '900', color: '#666' }}>{(FIXED_MAX_Y/2).toFixed(1)}</Typography>
                <Typography style={{ position: 'absolute', right: 5, top: 115, fontSize: 8, fontWeight: '900', color: '#666' }}>0</Typography>
                <Typography style={{ position: 'absolute', left: -5, top: 55, fontSize: 7, fontWeight: '900', color: '#444', transform: [{ rotate: '-90deg' }] } as any}>G/MIN</Typography>
            </Box>

            <Box style={{ position: 'relative', height: 120 }}>
                <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ overflow: 'hidden' }}>
                    {[0.25, 0.5, 0.75].map(tick => (
                        <line key={tick} x1="0" y1={98 - tick * 98} x2="100" y2={98 - tick * 98} stroke="#333" strokeWidth="0.5" strokeDasharray="2,2" opacity="0.2" />
                    ))}
                    <line x1="0" y1="98" x2="100" y2="98" stroke="#333" strokeWidth="1" vectorEffect="non-scaling-stroke" />

                    {visibleLines.windows && stateWindows.map((w, i) => (
                        <g key={i}>
                            <rect x={w.startX} y="0" width={w.endX - w.startX} height="98" fill={w.color} />
                        </g>
                    ))}

                    <path d={paths.gastric} fill="rgba(50,50,50,0.2)" stroke="#333" strokeWidth={visibleLines.gastric ? 1 : 0} vectorEffect="non-scaling-stroke" />
                    <path d={paths.protein} fill="none" stroke="#3b82f6" strokeWidth={visibleLines.protein ? 1.5 : 0} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                    <path d={paths.glucose} fill="none" stroke="#2ecc71" strokeWidth={visibleLines.glucose ? 1.5 : 0} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                    <path d={paths.cgm} fill="none" stroke="#2ecc71" strokeWidth={visibleLines.cgm ? 1.5 : 0} strokeDasharray="2,2" opacity={0.5} vectorEffect="non-scaling-stroke" />
                    <path d={paths.fructose} fill="none" stroke="#00ffff" strokeWidth={visibleLines.fructose ? 1.5 : 0} strokeDasharray="4,4" vectorEffect="non-scaling-stroke" />
                    <path d={paths.starch} fill="none" stroke="#a855f7" strokeWidth={visibleLines.starch ? 1.5 : 0} vectorEffect="non-scaling-stroke" />
                    <path d={paths.fat} fill="none" stroke="#ef4444" strokeWidth={visibleLines.fat ? 1.5 : 0} vectorEffect="non-scaling-stroke" />
                    <path d={paths.insulin} fill="none" stroke="#f29b11" strokeWidth={visibleLines.insulin ? 1 : 0} strokeDasharray="2,2" vectorEffect="non-scaling-stroke" />
                    
                    {isToday && nowX !== null && nowX >= 0 && nowX <= 100 && (
                        <line x1={nowX} y1="0" x2={nowX} y2="98" stroke="#ef4444" strokeWidth="1" opacity="0.8" vectorEffect="non-scaling-stroke" />
                    )}
                </svg>

                <Box style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, pointerEvents: 'none' } as any}>
                    {macroPeaks.map((p, i) => (
                        <Box key={`peak-${i}`} style={{ position: 'absolute', left: `${p.x}%`, top: `${p.y}%`, width: 0, height: 0, alignItems: 'center', justifyContent: 'center' } as any}>
                            <Box style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: p.color }} />
                            <Typography style={{ position: 'absolute', bottom: 6, fontSize: 8, fontWeight: '900', color: p.color, textShadow: '0 0 3px #000', width: 40, textAlign: 'center' }}>{p.val}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Box style={{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 5, marginTop: 10 }}>
                {['00', '06', '12', '18', '24'].map(h => (
                    <Typography key={h} style={{ fontSize: 11, color: '#666', fontWeight: '900', ...numericStyle }}>{h}</Typography>
                ))}
            </Box>
        </Box>
    );
};
